import { useLayoutEffect, useRef } from "react";

import { observer } from "mobx-react-lite";

import { SearchInput } from "@/components/search/SearchInput";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";
import { useRovingNav } from "@/utils/useRovingNav";

import type { ComponentProps, RefObject } from "react";

export const ControlsRow = observer(function ControlsRow({
  children,
  className,
  contentClassName,
  toolbarLabel,
  searchButtonRef,
  ...props
}: ComponentProps<"div"> & {
  contentClassName?: string;
  /** Names the buttons' toolbar: one Tab stop, Left/Right between buttons */
  toolbarLabel: string;
  /** Gets focus back when the user closes search from its input */
  searchButtonRef: RefObject<HTMLButtonElement | null>;
}) {
  const { search, view } = useStore();
  const isSearchOpen = search.isOpen && !view.isLeftSidebarCollapsed;
  const toolbarRef = useRef<HTMLDivElement>(null);
  useRovingNav(toolbarRef, "horizontal");
  const restoreFocusRef = useRef(false);

  // Runs once the buttons are no longer inert
  useLayoutEffect(() => {
    if (isSearchOpen || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    searchButtonRef.current?.focus();
  }, [isSearchOpen, searchButtonRef]);

  return (
    <div className={twMerge("relative", className)} {...props}>
      <div
        ref={toolbarRef}
        role="toolbar"
        aria-orientation="horizontal"
        aria-label={toolbarLabel}
        data-testid="controls-row-content"
        inert={isSearchOpen}
        className={twMerge("flex w-full items-center", contentClassName)}
      >
        {children}
      </div>
      {isSearchOpen && (
        <SearchInput
          className={twMerge(
            "absolute inset-0 min-h-0 h-full mb-0 rounded-6 p-0.5 @container",
            "shadow-none dark:shadow-none animate-search-reveal motion-reduce:animate-none will-change-[clip-path]",
          )}
          onClosed={() => {
            restoreFocusRef.current = true;
          }}
        />
      )}
    </div>
  );
});
